<script setup>
import {computed} from 'vue';
import {useAuthService} from '~/composables/useAuthService';
import {messageTimeLabel} from '~/composables/useMessageTime.js';
import {useDeviceLocale} from '~/composables/useDeviceLocale';
import {useDates} from '~/composables/useDates';
import AvatarImage from '@/components/images/AvatarImage.vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true
  },
  isActive: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['click', 'delete']);

const {user} = useAuthService();

// Get the other participant for one-to-one chats (data already loaded in conversation.participants)
const otherParticipant = computed(() => {
  if (props.conversation.conversationType !== 1) return null;
  if (!props.conversation.participants) return null;
  return props.conversation.participants.find(p => p.id !== user.value?.id);
});

// Conversation title
const conversationTitle = computed(() => {
  if (props.conversation.title) {
    return props.conversation.title;
  }

  // For one-to-one chats, show other participant's name
  if (props.conversation.conversationType === 1 && otherParticipant.value) {
    return `${otherParticipant.value.firstName} ${otherParticipant.value.lastName}`;
  }

  // Fallback
  return 'Conversation';
});

// Conversation initials/fallback for avatar
const conversationInitials = computed(() => {
  if (props.conversation.conversationType === 1 && otherParticipant.value) {
    const first = otherParticipant.value.firstName?.charAt(0) || '';
    const last = otherParticipant.value.lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || '?';
  }

  const title = conversationTitle.value;
  if (!title) return '?';

  const words = title.split(' ');
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
});

// Avatar filename for one-to-one chats
const avatarFilename = computed(() => {
  if (props.conversation.conversationType === 1 && otherParticipant.value) {
    return otherParticipant.value.imageFileName;
  }
  return null;
});

// Last message preview
const lastMessagePreview = computed(() => {
  const content = props.conversation.lastMessageContent;

  // If we have content with actual text, show it
  if (content && content.trim()) {
    return content;
  }

  // If no content but we have a lastMessageAt timestamp,
  // assume it's an image message without a caption
  if (props.conversation.lastMessageAt && content === '') {
    return '📷 Image';
  }

  // Truly no messages (X223: none since this viewer joined, when the chat has older ones kept back)
  return props.conversation.earlierMessagesHidden ? 'No messages since you joined' : 'No messages yet';
});

// The latest message's stamp: Now, 5m, 3h, 2d, else the date (useMessageTime).
const deviceLocale = useDeviceLocale();
const formattedTime = computed(() => {
  if (!props.conversation.lastMessageAt) return '';
  return messageTimeLabel(props.conversation.lastMessageAt, new Date(), deviceLocale.value || useDates().currentLocale());
});

// Check if current user has left this conversation
const hasLeftConversation = computed(() => {
  if (!user.value?.id) return false;
  return !props.conversation.participantUserIds?.includes(user.value.id);
});
</script>

<template>
  <div
      class="convo"
      :class="{ sel: isActive, left: hasLeftConversation }"
      @click="$emit('click', conversation)"
  >
    <span class="av">
      <avatar-image
          :filename="avatarFilename"
          :fallback="conversationInitials"
          :size="44"
          class="convo-face"
      />
      <span v-if="conversation.unreadCount > 0" class="ubadge">{{ conversation.unreadCount }}</span>
    </span>

    <div class="who">
      <div class="t" :class="{ unread: conversation.unreadCount > 0 }">
        {{ conversationTitle }}
        <span v-if="hasLeftConversation" class="left-tag">Left</span>
      </div>
      <div class="s" :class="{ unread: conversation.unreadCount > 0 }">{{ lastMessagePreview }}</div>
    </div>

    <div class="right">
      <span v-if="conversation.lastMessageAt" class="time">{{ formattedTime }}</span>

      <v-icon v-if="conversation._syncState === 'pending'" size="14" color="textSubtle">mdi-clock-outline</v-icon>
      <v-icon v-else-if="conversation._syncState === 'failed'" size="14" color="error">mdi-alert-circle</v-icon>

      <crud-confirm-dialog
          v-else
          title="Delete Conversation"
          message="Are you sure you want to delete this conversation? This action cannot be undone."
          confirm-text="Delete"
          @confirm="emit('delete', conversation)"
      >
        <template #default="{ click }">
          <v-menu>
            <template #activator="{ props }">
              <button type="button" class="convo-menu" v-bind="props" aria-label="Conversation options" @click.stop>
                <v-icon size="16">mdi-dots-vertical</v-icon>
              </button>
            </template>
            <v-list density="compact">
              <v-list-item @click="click">
                <template #prepend>
                  <v-icon color="error" size="18">mdi-delete</v-icon>
                </template>
                <v-list-item-title>Delete</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </crud-confirm-dialog>
    </div>
  </div>
</template>

<style scoped lang="scss">
.convo {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 16px;
  cursor: pointer;

  & + .convo {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
  }

  &:hover {
    background: rgb(var(--v-theme-bg, 250, 250, 250));
  }

  &.sel {
    background: rgb(var(--v-theme-brandTint));
  }

  &.left {
    opacity: 0.6;
  }
}

.av {
  position: relative;
  flex-shrink: 0;
  display: inline-flex;
}

.convo-face :deep(.v-img),
.convo-face :deep(.v-avatar) {
  border-radius: 50%;
}

.ubadge {
  position: absolute;
  top: -3px;
  right: -3px;
  min-width: 19px;
  height: 19px;
  border-radius: 10px;
  background: rgb(var(--v-theme-primary));
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  border: 2px solid rgb(var(--v-theme-surface));
}

.who {
  flex: 1;
  min-width: 0;
}

.who .t {
  font-size: 13.5px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.unread {
    font-weight: 700;
  }
}

.who .s {
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.unread {
    color: rgb(var(--v-theme-charcoal));
    font-weight: 600;
  }
}

.left-tag {
  display: inline-block;
  font-size: 9.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-textSubtle));
  background: rgb(var(--v-theme-light));
  border-radius: 999px;
  padding: 1px 7px;
  margin-left: 6px;
  vertical-align: middle;
}

.right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
  flex-shrink: 0;
}

.right .time {
  font-size: 10.5px;
  color: rgb(var(--v-theme-textSubtle));
  font-weight: 500;
}

.convo-menu {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgb(var(--v-theme-textSubtle));

  &:hover {
    background: rgb(var(--v-theme-light));
  }
}
</style>
